import React, { useEffect, useState } from 'react';
import MetricCard from './primitives/MetricCard';
import {
  getRiTotalRoutes, getRiOriginMarkets, getRiMedianDriveTime, getRiAvgGti,
} from '../../api/auth';
import { fmtUsd } from '../../utils/format';

function fmtInt(n) {
  if (n == null || n === '') return null;
  return Number(n).toLocaleString();
}

function fmtHours(n) {
  if (n == null || n === '') return null;
  return Number(n).toFixed(1);
}

/**
 * Footer copy for the Avg Guest Trip Investment card.
 *
 * Keeps three states distinct that were previously collapsed into one
 * sentence: in-flight asserts nothing, an unavailable read says so, and only a
 * settled response actually carrying zero booked guests earns the empty-state
 * line. `fn_get_ri_avg_gti` times out (Postgres 57014) on a cold read, so the
 * old single-branch version told users their park had no booked guests while
 * the request was still pending or had failed — 263 of them, in the case that
 * surfaced this.
 *
 * @param {object}        args
 * @param {boolean}       args.loading Requests still in flight.
 * @param {object|null}   args.gti     `fn_get_ri_avg_gti` payload, or null when
 *   the read failed or returned nothing.
 * @returns {import('react').ReactNode|null} Footer content, or null while
 *   loading (the card renders a skeleton then, so there is nothing to caption).
 */
function tripInvestmentFooter({ loading, gti }) {
  if (loading) return null;
  if (!gti) return "Trip investment couldn't be loaded — try again shortly";
  if (gti.booked_guests > 0) {
    return `${fmtUsd(gti.avg_site_cost)} Site + ${fmtUsd(gti.avg_fuel_cost)} Fuel · ${fmtInt(gti.booked_guests)} Booked`;
  }
  return 'No booked guests with route data in this date range';
}

export default function HeadlineMetrics({ clientId, range }) {
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setState({ loading: true });
    Promise.all([
      getRiTotalRoutes(clientId, { from, to }).catch(() => null),
      getRiOriginMarkets(clientId, { from, to }).catch(() => null),
      getRiMedianDriveTime(clientId, { from, to }).catch(() => null),
      getRiAvgGti(clientId, { from, to }).catch(() => null),
    ]).then(([routes, markets, drive, gti]) => {
      if (cancelled) return;
      setState({
        loading: false,
        routes: routes?.data || null,
        markets: markets?.data || null,
        drive: drive?.data || null,
        gti: gti?.data || null,
      });
    });
    return () => { cancelled = true; };
  }, [clientId, from, to]);

  const { loading, routes, markets, drive, gti } = state;

  const accuracyHint = routes
    ? [
        routes.address_tier ? `Address: ${fmtInt(routes.address_tier)}` : null,
        routes.postal_tier  ? `Postal: ${fmtInt(routes.postal_tier)}`  : null,
        routes.weather_tier ? `Weather: ${fmtInt(routes.weather_tier)}` : null,
        routes.ip_tier      ? `IP: ${fmtInt(routes.ip_tier)}`          : null,
      ].filter(Boolean).join(' · ') || null
    : null;

  // Keep the card single-framed on US geographic reach: the headline is
  // "{N} US States", so the footer stays US-centric ("{N} Unique Cities") and
  // omits the Non-US Origins count, which pulled against the headline (markup
  // #82).
  const marketsFooter = markets
    ? `${fmtInt(markets.unique_cities)} Unique Cities`
    : null;

  const gtiFooter = tripInvestmentFooter({ loading, gti });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        label="Total Routes Computed"
        tone="brandBlue"
        icon="share"
        // Guard on `routes` rather than defaulting the addends to 0: with the
        // read failed, `0 + 0` rendered a confident "0" for routes computed
        // instead of admitting it doesn't know.
        value={routes ? fmtInt((routes.total_routes || 0) + (routes.unreachable || 0)) : null}
        hint={routes?.unreachable ? (
          <span title="Routes whose origin couldn't be geocoded (no usable address, postal, or IP geo). They are counted in the total above but are not part of the tier breakdown below.">
            Includes {fmtInt(routes.unreachable)} that couldn't be geocoded
          </span>
        ) : null}
        footer={accuracyHint}
        loading={loading}
      />
      <MetricCard
        label="Origin Markets"
        tone="purple"
        icon="flag"
        value={loading ? null : (markets ? `${fmtInt(markets.unique_states)} US States` : null)}
        footer={marketsFooter}
        loading={loading}
      />
      <MetricCard
        label="Median Drive Time"
        tone="amber"
        icon="clock"
        value={loading ? null : fmtHours(drive?.median_hours)}
        suffix={drive?.median_hours != null ? ' hrs' : ''}
        footer={drive && (drive.avg_hours != null || drive.max_hours != null)
          ? `Avg: ${fmtHours(drive.avg_hours) ?? '—'} hrs · Max: ${fmtHours(drive.max_hours) ?? '—'} hrs`
          : null}
        loading={loading}
      />
      <MetricCard
        label="Avg Guest Trip Investment"
        tone="emerald"
        icon="dollar"
        value={loading ? null : (gti?.avg_gti ? fmtUsd(gti.avg_gti) : null)}
        footer={gtiFooter}
        loading={loading}
      />
    </div>
  );
}
